import { useEffect, useRef, useState } from "react";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

type BackupRestoreKind = "backup" | "restore";

/** Status codes from C++ `Ti_store_backup_progress` / `Ti_store_restore_progress`. */
const STATUS_IN_PROGRESS = 1;
const STATUS_SUCCESS = 3;
const STATUS_NO_DATA = 4; // restore only
const STATUS_FAIL = 0;
// Any other status closes the dialog with no prompt (C++ kills the dialog only).

interface ProgressEvent {
  config: string;
  turbine: string;
  percent: number;
  /** Operation window + the date reached, pre-formatted `DD/MM/YYYY` (or ""). */
  start_date: string;
  current_date: string;
  end_date: string;
  status: number;
}

interface ActiveProgress extends ProgressEvent {
  kind: BackupRestoreKind;
}

const TITLE_KEY: Record<BackupRestoreKind, string> = {
  backup: "TIGER_BACKUP_DATA_TITLE",
  restore: "TIGER_RESTORE_DATA_TITLE",
};
const PROGRESS_MSG_KEY: Record<BackupRestoreKind, string> = {
  backup: "TIGER_BACKUP_PROGRESS_MSG",
  restore: "TIGER_RESTORE_PROGRESS_MSG",
};
const CANCEL_CMD: Record<BackupRestoreKind, string> = {
  backup: "cancel_backup",
  restore: "cancel_restore",
};

/**
 * Shown while a data backup / restore is in flight. Opens on the first in-flight
 * `backup-progress` / `restore-progress` event (status 1, emitted per day by
 * the in-process backup / restore job), advances the bar as more arrive, and
 * resolves on a terminal status: 3 → success info, 0 → failure error, 4 → "no
 * data" (restore only). The Cancel button stops the job via the cancel command. Mirrors the C++ `Ti_store_backup_progress` /
 * `Ti_store_restore_progress` dialog + the success/fail/no-data prompts.
 *
 * Styled to match the export progress dialog. Self-contained: manages its own
 * visibility, so it only needs mounting once.
 */
export function BackupRestoreProgressDialog() {
  const [progress, setProgress] = useState<ActiveProgress | null>(null);
  // Set when the user cancels so a trailing terminal event doesn't pop a
  // success/error prompt for an operation they deliberately aborted.
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: Array<() => void> = [];

    const handle = async (kind: BackupRestoreKind, p: ProgressEvent) => {
      if (p.status === STATUS_IN_PROGRESS) {
        setProgress({ ...p, kind });
        return;
      }
      // Non-progress status — close the progress dialog. Mirror the C++
      // `Ti_store_*_progress` exactly: only 3 / 4 / 0 produce a prompt; ANY
      // other status (e.g. an intermediate the exe streams before the final 3)
      // just closes the dialog with no message. Treating "anything not success"
      // as a failure is what made a successful restore flash "failed" first.
      setProgress(null);
      const isSuccess = p.status === STATUS_SUCCESS;
      const isNoData = kind === "restore" && p.status === STATUS_NO_DATA;
      const isFail = p.status === STATUS_FAIL;
      if (!isSuccess && !isNoData && !isFail) {
        return; // unrecognised status: close silently, like C++
      }
      if (cancelledRef.current) {
        cancelledRef.current = false;
        return; // user aborted: no prompt
      }
      const { message } = await import("@tauri-apps/plugin-dialog");
      const title = getTigerResource(TITLE_KEY[kind]);
      if (isSuccess) {
        await message(
          getTigerResource(kind === "backup" ? "TIGER_BACKUP_SUCCESS" : "TIGER_RESTORE_SUCCESS"),
          { title, kind: "info" },
        );
      } else if (isNoData) {
        await message(getTigerResource("TIGER_RESTORE_NO_DATA"), { title, kind: "warning" });
      } else {
        await message(
          getTigerResource(kind === "backup" ? "TIGER_BACKUP_FAIL" : "TIGER_RESTORE_FAIL"),
          { title, kind: "error" },
        );
      }
    };

    void import("@tauri-apps/api/event").then(async ({ listen }) => {
      const offBackup = await listen<ProgressEvent>("backup-progress", (e) =>
        void handle("backup", e.payload),
      );
      const offRestore = await listen<ProgressEvent>("restore-progress", (e) =>
        void handle("restore", e.payload),
      );
      if (cancelled) {
        offBackup();
        offRestore();
      } else {
        offs.push(offBackup, offRestore);
      }
    });

    return () => {
      cancelled = true;
      offs.forEach((off) => off());
    };
  }, []);

  if (!progress) return null;

  const { kind } = progress;
  const pct = Math.max(0, Math.min(100, progress.percent));
  const title = getTigerResource(TITLE_KEY[kind]);
  const subtitle = tigerFormat(
    getTigerResource(PROGRESS_MSG_KEY[kind]),
    progress.start_date || "—",
    progress.end_date || "—",
  );

  const onCancel = async () => {
    cancelledRef.current = true;
    setProgress(null); // close immediately
    if (!isTauri()) return;
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke(CANCEL_CMD[kind]);
  };

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button className="acrylic-close-btn" onClick={onCancel} title={getTigerResource("TIGER_RES_CANCEL")}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <div style={styles.card}>
            <div style={styles.label}>
              {progress.turbine ? (
                <>
                  {subtitle} (<b>{progress.turbine}</b>)
                </>
              ) : (
                subtitle
              )}
            </div>
            <div style={styles.barOuter}>
              <div style={{ ...styles.barInner, width: `${pct}%` }} />
              <div style={styles.barLabel}>{pct.toFixed(0)}%</div>
            </div>
            {/* The operation sweeps Start → End, with Current as the live date. */}
            <div style={styles.timeline}>
              <div style={styles.timeCol}>
                <span style={styles.timeLabel}>{getTigerResource("TIGER_RES_START_DATE") || "Start"}</span>
                <span style={styles.timeValue}>{progress.start_date || "—"}</span>
              </div>
              <div style={{ ...styles.timeCol, ...styles.timeColCenter }}>
                <span style={styles.timeLabel}>{getTigerResource("TIGER_PROCESSING") || "Current"}</span>
                <span style={{ ...styles.timeValue, ...styles.timeValueCurrent }}>
                  {progress.current_date || "—"}
                </span>
              </div>
              <div style={{ ...styles.timeCol, ...styles.timeColRight }}>
                <span style={styles.timeLabel}>{getTigerResource("TIGER_RES_END_DATE") || "End"}</span>
                <span style={styles.timeValue}>{progress.end_date || "—"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={onCancel}>
            {getTigerResource("TIGER_RES_CANCEL") || "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  overlay: {
    ...acrylicStyles.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    background: "var(--ui-surface)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: "1px solid rgba(255,255,255,0.65)",
    borderRadius: 14,
    width: 440,
    maxWidth: "95vw",
    boxShadow: "0 16px 48px rgba(20,30,60,0.35)",
    overflow: "hidden" as const,
    display: "flex",
    flexDirection: "column" as const,
    fontFamily: acrylicTokens.font,
    color: acrylicTokens.textPrimary,
  },
  barOuter: {
    position: "relative" as const,
    height: 20,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(120,130,150,0.35)",
    borderRadius: 7,
    overflow: "hidden",
    marginTop: 12,
  },
  barInner: {
    height: "100%",
    background: "var(--ui-btn-primary-bg)",
    transition: "width 0.2s ease",
  },
  barLabel: {
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
    color: "#4b5260",
  },
  timeline: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 16,
  },
  timeCol: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start" as const,
    gap: 1,
  },
  timeColCenter: {
    alignItems: "center" as const,
    flex: 1,
  },
  timeColRight: {
    alignItems: "flex-end" as const,
  },
  timeLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase" as const,
    color: acrylicTokens.textMuted,
  },
  timeValue: {
    fontSize: 13,
    fontVariantNumeric: "tabular-nums",
    color: acrylicTokens.textPrimary,
    marginTop: 2,
  },
  timeValueCurrent: {
    fontSize: 17,
    fontWeight: 700,
    color: acrylicTokens.accent,
    lineHeight: 1.1,
  },
} as const;
